import * as dotProp from 'dot-prop-immutable';
import { Action } from 'typescript-fsa';

import { createReducer } from '@topconagriculture/frontend-utils';

import {
  resetRedirectPath,
  setActiveTabIndex,
  setEditGroupPropertyTooltipName,
  setFeedingListFormIsChanged,
  setFeedingListVisited,
  setRedirectPath,
  setSidebarVisibility,
} from './ui.actions';
import { SidebarNames, TabsNames } from './ui.constants';

export type UIState = {
  readonly tabs: {
    [key in TabsNames]?: {
      activeTabIndex: number;
      tabName: string;
    };
  };
  readonly sidebars: {
    [key in SidebarNames]?: {
      isVisible: boolean;
    };
  };

  readonly preventNavigation: {
    readonly status: boolean;
    readonly redirectPath: string | null;
  };
  readonly feedGroups: {
    readonly editGroupPropertyTooltipName: string | null;
  };
  readonly feedingListVisited: boolean;
  readonly feedingListFormIsChanged: boolean;
};

export const initialState: UIState = {
  tabs: {},
  sidebars: {
    products: {
      isVisible: true,
    },
  },
  feedGroups: {
    editGroupPropertyTooltipName: null,
  },
  preventNavigation: {
    status: false,
    redirectPath: null,
  },
  feedingListVisited: false,
  feedingListFormIsChanged: false,
};

const tabsInitialState = {};

const sideBarsInitialState = {
  products: {
    isVisible: true,
  },
};

const feedGroupsInitialState = {
  editGroupPropertyTooltipName: null,
};

const preventNavigationInitialState = {
  status: false,
  redirectPath: null,
};

const feedingListVisitedInitialState = false;
const feedingListFormIsChangedInitialState = false;

export const tabsReducer = createReducer(tabsInitialState, {
  [setActiveTabIndex.type]: (
    state: UIState,
    action: Action<{ tabIndex: number; tabName: string; tabNamespace: TabsNames }>,
  ) => {
    return dotProp.set(state, `${action.payload.tabNamespace}`, {
      activeTabIndex: action.payload.tabIndex,
      tabName: action.payload.tabName,
    });
  },
});

const preventNavigationReducer = createReducer(preventNavigationInitialState, {
  [setRedirectPath.type]: (state: UIState, action: Action<string>) => {
    return dotProp.set(state, `redirectPath`, action.payload);
  },
  [resetRedirectPath.type]: (state: UIState) => {
    return dotProp.set(state, `redirectPath`, null);
  },
});

const feedGroupsReducer = createReducer(feedGroupsInitialState, {
  [setEditGroupPropertyTooltipName.type]: (state: UIState, action: Action<string | null>) =>
    dotProp.set(state, 'editGroupPropertyTooltipName', action.payload),
});

const sideBarsReducer = createReducer(sideBarsInitialState, {
  [setSidebarVisibility.type]: (state: UIState, action: Action<{ sidebarName: SidebarNames; isVisible: boolean }>) => {
    return dotProp.set(state, `${action.payload.sidebarName}.isVisible`, action.payload.isVisible);
  },
});

const feedingListVisitedReducer = createReducer(feedingListVisitedInitialState, {
  [setFeedingListVisited.type]: (state: UIState, action: Action<boolean>) =>
    dotProp.set(state, 'feedingListVisited', action.payload),
});

const feedingListFormIsChangedReducer = createReducer(feedingListFormIsChangedInitialState, {
  [setFeedingListFormIsChanged.type]: (state: UIState, action: Action<boolean>) => action.payload,
});

export const tabsReducerContainer = {
  tabs: tabsReducer,
};

export const preventNavigationReducerContainer = {
  preventNavigation: preventNavigationReducer,
};

export const feedGroupsReducerContainer = {
  feedGroups: feedGroupsReducer,
};

export const sideBarsReducerContainer = {
  sidebars: sideBarsReducer,
};

export const feedingListVisitedReducerContainer = {
  feedingListVisited: feedingListVisitedReducer,
};

export const feedingListFormIsChangedReducerContainer = {
  feedingListFormIsChanged: feedingListFormIsChangedReducer,
};
